import type {
  McpServerDto,
  SkillDetailDto,
  SkillDto,
  SkillUsageDto,
  TeamDto,
  TeamMemberDto,
  WebPolicyDto,
} from '@dbrain/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from './client.js';

/**
 * Skills, connected services (MCP), teams and the organization's web and
 * integration policy (Phase 11). Lists are read for a place — a workspace
 * and, inside a conversation, the conversation — because what is on or off
 * depends on it (SA-049).
 */
export const capKeys = {
  skillsAll: ['skills'] as const,
  skills: (where: string) => ['skills', 'list', where] as const,
  skill: (key: string, where: string) => ['skills', 'detail', key, where] as const,
  usage: (days: number) => ['skills', 'usage', days] as const,
  review: ['skills', 'review'] as const,
  serversAll: ['mcp'] as const,
  servers: (where: string) => ['mcp', 'list', where] as const,
  teams: ['teams'] as const,
  teamMembers: (id: string) => ['teams', id, 'members'] as const,
  webPolicy: ['web-policy'] as const,
};

export interface Place {
  workspaceId?: string | undefined;
  conversationId?: string | undefined;
}

export function placeQuery(place: Place = {}): string {
  const params = new URLSearchParams();
  if (place.workspaceId) params.set('workspaceId', place.workspaceId);
  if (place.conversationId) params.set('conversationId', place.conversationId);
  const query = params.toString();
  return query ? `?${query}` : '';
}

const whereOf = (place: Place = {}) => `${place.workspaceId ?? ''}:${place.conversationId ?? ''}`;

/** `keepPrevious`: while the list of a new place loads, the last one read stays (marked as placeholder data). */
export const useSkills = (place: Place = {}, enabled = true, keepPrevious = false) =>
  useQuery({
    queryKey: capKeys.skills(whereOf(place)),
    queryFn: () => api<SkillDto[]>('GET', `/api/skills${placeQuery(place)}`),
    enabled,
    staleTime: 15_000,
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });

export const useSkill = (key: string | null, place: Place = {}) =>
  useQuery({
    queryKey: capKeys.skill(key ?? '', whereOf(place)),
    queryFn: () => api<SkillDetailDto>('GET', `/api/skills/${encodeURIComponent(key as string)}${placeQuery(place)}`),
    enabled: Boolean(key),
  });

export const useSkillUsage = (days: number) => useQuery({ queryKey: capKeys.usage(days), queryFn: () => api<SkillUsageDto[]>('GET', `/api/skills/usage?days=${days}`) });
/** one skill's own usage, for its managers (D-118); null: nothing to ask */
export const useSkillUsageOf = (skillId: string | null, days: number) =>
  useQuery({
    queryKey: [...capKeys.usage(days), skillId],
    queryFn: () => api<{ days: number; loads: number; users: number }>('GET', `/api/skills/${skillId}/usage?days=${days}`),
    enabled: skillId !== null,
  });

export interface ReviewItem {
  skillId: string;
  versionId: string;
  version: number;
  name: string;
  description: string;
  submittedAt: string | null;
  permissions: SkillDto['permissions'];
  skillMd: string;
}

export const useReviewQueue = (enabled: boolean) => useQuery({ queryKey: capKeys.review, queryFn: () => api<ReviewItem[]>('GET', '/api/skills/review'), enabled });

export const useServers = (place: Place = {}, enabled = true) =>
  useQuery({ queryKey: capKeys.servers(whereOf(place)), queryFn: () => api<McpServerDto[]>('GET', `/api/mcp/servers${placeQuery(place)}`), enabled, staleTime: 15_000 });

/** The connections page (D-118): one workspace's view, or everywhere with every workspace's own connections too. */
export const useLibraryServers = (workspaceId: string) =>
  useQuery({
    queryKey: capKeys.servers(workspaceId ? `w:${workspaceId}` : 'library'),
    queryFn: () => api<McpServerDto[]>('GET', `/api/mcp/servers?${workspaceId ? `workspaceId=${workspaceId}` : 'allWorkspaces=1'}`),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });

export const useTeams = () => useQuery({ queryKey: capKeys.teams, queryFn: () => api<TeamDto[]>('GET', '/api/teams') });

export const useTeamMembers = (teamId: string | null) =>
  useQuery({ queryKey: capKeys.teamMembers(teamId ?? ''), queryFn: () => api<TeamMemberDto[]>('GET', `/api/teams/${teamId}/members`), enabled: Boolean(teamId) });

export const useWebPolicy = () => useQuery({ queryKey: capKeys.webPolicy, queryFn: () => api<WebPolicyDto>('GET', '/api/tenant/web-policy') });
